/**
 * RevenueCat Web SDK wrapper.
 *
 * Environment variable (set in .env.local or the deploy environment):
 *   REACT_APP_RC_WEB_API_KEY — RevenueCat Web Billing public API key
 *
 * RevenueCat setup checklist (in the RC dashboard), same project mobile
 * already uses so the "pro" entitlement is shared across platforms:
 *   1. Add a Web Billing app to the project.
 *   2. Create monthly/annual products under RevenueCat Billing, add both
 *      to the existing "pro" entitlement.
 *   3. Create an offering named "default" containing both packages.
 *   4. Confirm the webhook URL (<API>/api/webhooks/revenuecat) and the
 *      Authorization header (REVENUECAT_WEBHOOK_AUTH) are configured —
 *      this is the same webhook mobile purchases already use.
 */
import { Purchases, ErrorCode, PurchasesError, type CustomerInfo, type Package } from '@revenuecat/purchases-js';
import { intervalFromProductId, type PlanInterval, type RcStore } from '../utils/subscriptionPlan';

const RC_ENTITLEMENT = 'pro';
const RC_API_KEY = process.env.REACT_APP_RC_WEB_API_KEY ?? '';

export interface PlanPackages {
    monthly: Package | null;
    annual: Package | null;
}

export const purchaseService = {
    /**
     * Must be called once after the user's ID is known (post-login / token
     * restore) — mirrors weavewords-mobile's purchaseService.configure.
     * Passing the user's numeric ID as appUserId lets the backend correlate
     * RevenueCat webhook events to the right user row.
     *
     * Unlike the mobile SDK, the web SDK's singleton survives across a
     * logout/login within the same tab (no app relaunch to reset it), so a
     * bare "configure once" would leave the RC identity pinned to whichever
     * user logged in first — every purchase/managementURL call after a
     * second account logs in on the same tab would then act on the first
     * user's RevenueCat identity. Re-identify via changeUser whenever the
     * requested user differs from whoever the SDK is currently configured
     * for.
     */
    configure(userId: number): void {
        if (!RC_API_KEY) {
            console.warn('purchaseService: REACT_APP_RC_WEB_API_KEY not set');
            return;
        }
        const appUserId = String(userId);
        if (!Purchases.isConfigured()) {
            Purchases.configure({ apiKey: RC_API_KEY, appUserId });
            return;
        }
        const instance = Purchases.getSharedInstance();
        if (instance.getAppUserId() !== appUserId) {
            instance.changeUser(appUserId).catch(err => console.warn('purchaseService.configure: changeUser failed', err));
        }
    },

    /**
     * Detaches the RC SDK from the departing user's identity on logout, so
     * it doesn't stay pinned to them if a different account logs in next on
     * the same tab (see the note on configure() above).
     */
    logOut(): void {
        if (!Purchases.isConfigured()) return;
        Purchases.getSharedInstance()
            .changeUser(Purchases.generateRevenueCatAnonymousAppUserId())
            .catch(err => console.warn('purchaseService.logOut: changeUser failed', err));
    },

    /**
     * Whether purchases can work at all: false when the build has no
     * REACT_APP_RC_WEB_API_KEY (configure() then never sets the SDK up) —
     * which was the production web build's state until 2026-10-20, so
     * every plan click threw. Callers hide their buy buttons instead.
     */
    isAvailable(): boolean {
        return Purchases.isConfigured();
    },

    /** Returns the monthly/annual packages from the default RC offering. */
    async getPackages(): Promise<PlanPackages> {
        if (!Purchases.isConfigured()) return { monthly: null, annual: null };
        const offerings = await Purchases.getSharedInstance().getOfferings();
        return {
            monthly: offerings.current?.monthly ?? null,
            annual: offerings.current?.annual ?? null,
        };
    },

    /**
     * Opens RevenueCat's checkout widget for the given package. Resolves
     * with the resulting CustomerInfo on success; resolves `null` if the
     * user closed/cancelled the checkout (not an error state — every other
     * failure still throws).
     */
    async purchase(pkg: Package, customerEmail?: string): Promise<CustomerInfo | null> {
        try {
            const result = await Purchases.getSharedInstance().purchase({ rcPackage: pkg, customerEmail });
            return result.customerInfo;
        } catch (err) {
            if (err instanceof PurchasesError && err.errorCode === ErrorCode.UserCancelledError) {
                return null;
            }
            throw err;
        }
    },

    /**
     * Looks up the right package for `interval` and purchases it — the
     * shared "upgrade to Pro" entry point used by both SubscriptionTab
     * (account settings) and ProfileSetup's onboarding paywall, so the
     * monthly/annual → RC package lookup only lives in one place. Throws
     * if the package isn't found (e.g. RC Billing not yet configured with
     * both products in the dashboard) or on any purchase error other than
     * user cancellation; resolves `null` on cancellation.
     */
    async purchaseInterval(interval: 'monthly' | 'annual', customerEmail?: string): Promise<CustomerInfo | null> {
        const packages = await purchaseService.getPackages();
        const pkg = interval === 'annual' ? packages.annual : packages.monthly;
        if (!pkg) {
            const err = new Error(`purchaseService: no RevenueCat package found for interval "${interval}"`);
            err.name = 'NoPackageError'; // a stable funnel reason (NO_PACKAGE_ERROR), see purchaseErrorReason
            throw err;
        }
        return purchaseService.purchase(pkg, customerEmail);
    },

    /**
     * Length of the package's free trial in days, or 0 when it has none. The
     * trial is configured on the product in the RC dashboard (RC Web Billing),
     * and getOfferings only returns offers this customer is still entitled to,
     * so someone who already used the trial gets 0 here and is charged at once.
     */
    trialDays(pkg: Package | null | undefined): number {
        const period = pkg?.webBillingProduct.freeTrialPhase?.period;
        if (!period) return 0;
        const perUnit = { day: 1, week: 7, month: 30, year: 365 } as const;
        return period.number * (perUnit[period.unit] ?? 0);
    },

    /**
     * What the onboarding paywall shows, straight from the RC offering so it always matches
     * the checkout: localized prices, the annual free trial's length (0 = none for this
     * customer), and the annual saving vs. 12 months. Throws if RC can't be reached.
     */
    async getPaywallOffer(): Promise<PaywallOffer> {
        const { monthly, annual } = await purchaseService.getPackages();
        const monthlyMicros = monthly?.webBillingProduct.price.amountMicros ?? 0;
        const annualMicros = annual?.webBillingProduct.price.amountMicros ?? 0;
        const savingsPct = monthlyMicros > 0 && annualMicros > 0
            ? Math.round((1 - annualMicros / (monthlyMicros * 12)) * 100)
            : null;
        return {
            trialDays: purchaseService.trialDays(annual),
            monthlyPrice: monthly?.webBillingProduct.price.formattedPrice ?? null,
            annualPrice: annual?.webBillingProduct.price.formattedPrice ?? null,
            savingsPct: savingsPct !== null && savingsPct > 0 ? savingsPct : null,
        };
    },

    /** Whether the given CustomerInfo has an active "pro" entitlement. */
    isPro(info: CustomerInfo): boolean {
        return info.entitlements.active[RC_ENTITLEMENT] != null;
    },

    /**
     * What RevenueCat knows about this customer's Pro: whether there's an active
     * "pro" entitlement, which store it came from (Play, App Store, RC Web
     * Billing, promotional…), monthly or annual, and the RC web portal URL if
     * there is one (RC Web Billing customers only — it can exist without the
     * entitlement, e.g. a web product not attached to "pro").
     *
     * Throws when the SDK isn't configured (no key in this build, or called
     * before configure()) and when RC can't be reached, so callers can tell
     * "RC answered: nothing" apart from "RC didn't answer" — see
     * utils/subscriptionPlan.ts resolveManagement.
     */
    async getActivePlan(): Promise<ActivePlan> {
        if (!Purchases.isConfigured()) throw new Error('purchaseService: RevenueCat is not configured');
        const instance = Purchases.getSharedInstance();
        const info = await instance.getCustomerInfo();
        const ent = info.entitlements.active[RC_ENTITLEMENT];
        if (!ent) {
            return { entitled: false, store: null, interval: null, managementURL: info.managementURL };
        }
        // The product id usually names its interval; only ask for the offerings when it doesn't.
        let interval = intervalFromProductId(ent.productIdentifier);
        if (!interval) {
            try {
                const packages = await purchaseService.getPackages();
                interval = intervalFromProductId(ent.productIdentifier, {
                    monthly: packages.monthly?.webBillingProduct.identifier,
                    annual: packages.annual?.webBillingProduct.identifier,
                });
            } catch {
                // Offerings are only a hint for the interval.
            }
        }
        return {
            entitled: true,
            store: ent.store,
            interval,
            managementURL: info.managementURL,
        };
    },
};

/** The onboarding paywall's plan data, from RevenueCat (see getPaywallOffer). */
export interface PaywallOffer {
    /** Free-trial length on the annual plan; 0 when there's none (or this customer used it). */
    trialDays: number;
    /** Localized prices as the checkout will charge them, e.g. "€4.99"; null if RC lacks the plan. */
    monthlyPrice: string | null;
    annualPrice: string | null;
    /** Whole-percent annual saving vs. 12 × monthly, or null when unknown / none. */
    savingsPct: number | null;
    /** RC couldn't be reached — no trial is promised and no prices are shown. */
    failed?: boolean;
}

export const FAILED_PAYWALL_OFFER: PaywallOffer = {
    trialDays: 0, monthlyPrice: null, annualPrice: null, savingsPct: null, failed: true,
};

export interface ActivePlan {
    /** RevenueCat has an active "pro" entitlement for this customer. */
    entitled: boolean;
    /** The entitlement's store; null when not entitled. */
    store: RcStore | null;
    interval: PlanInterval | null;
    /** RC web billing portal — only meaningful for RC Web Billing customers. */
    managementURL: string | null;
}
